import type { Logger } from "@expert/logging";
import ky, { HTTPError } from "ky";
import { defaultHeaders, defaultTimeoutInMS } from "../types";
import type { DefaultPayload } from "./types";

type VerifyCustomerPayload = DefaultPayload & {
    mdn: string;
    pin: string;
};

export type ActivationFlowType = "REPLACEMENT_DEVICE" | "NEW_DEVICE";
export interface ClaimOrderSummaryForFrontend {
    claimId: string;
    deviceDescription: string;
    deviceSku?: string;
    shippedDate: string | null;
    insuranceType: string;
    ctn: string;
    secondaryCtn: string | null;
    isRequestedMdnMatch: boolean;
    requiresExtraAuth: boolean;
}

export interface VerifyCustomerResult {
    accountMdns: string[];
    activationFlowType: ActivationFlowType;
    claimSummaries: ClaimOrderSummaryForFrontend[];
    token: string;
    errorCode?: string;
    errorMessage?: string;
}

export const verifyCustomer = async (ssoAccessToken: string, payload: VerifyCustomerPayload, logger: Logger) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/api/device-activation/v1/verify-customer`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        const { accountMdns, activationFlowType, claimSummaries, token } = await resp.json<VerifyCustomerResult>();
        return { accountMdns, activationFlowType, claimSummaries, token };
    } catch (error) {
        logger.error({ error }, `Solve Guide | Error: Could not verify customer.`);

        let errorCode = "";
        let errorMessage = "";

        try {
            if (error instanceof HTTPError) {
                const errorJson = await error.response.json();
                errorCode = errorJson.code || "";
                errorMessage = errorJson.message || "";

                logger.info({ errorCode, errorMessage }, "Solve Guide | API returned error message");
            }
        } catch (parseError) {
            logger.error({ parseError }, "Solve Guide | Failed to parse error message");
        }

        return {
            accountMdns: [],
            activationFlowType: undefined,
            claimSummaries: [],
            token: undefined,
            errorCode,
            errorMessage,
        };
    }
};
